import { Tag } from 'antd';
import PermissionCheck from 'common/features/permission-check';
import Draggable from 'common/gantt/components/dnd/containers/dnd-draggable-container';
import { ItemTypes } from 'common/gantt/config';
import React from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import styled, { css } from 'styled-components';
import { Flex, Icon, SapId } from 'ui';
import moment from 'utils/cops-moment';
import { blockConfig } from '../../common/gantt/config';
import { originatorTagPrefix, ticketWorklistId } from '../../utils/app-configs';
import {
  determineSortIcon,
  isObjectNullOrEmpty,
  transformFiltersToUrlParams,
} from '../../utils/helpers';
import CustomDetailHeader from '../components/detail-header-component';
import GroupCellRenderer from '../components/group-cellrenderer';
import CustomHeader from '../components/header-component';
import IconColumn from '../components/iconcolumn';
import MasterDetailHeader from '../components/master-detail-header';
import RowActions from '../components/row-actions';
import WorkGridFilters from '../components/workgrid-filters';

const AlertIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts['1']};
  font-size: 16px;
`;

const WarningIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts['2']};
  font-size: 16px;
`;

const InfoIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts['3']};
  font-size: 16px;
`;

const LinkWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  cursor: pointer;
`;

const Link = styled.p`
  color: ${p => p.theme.colors.theme};
  text-overflow: ellipsis;
  overflow: hidden;
  &:hover {
    text-decoration: underline;
    text-decoration-color: ${p => p.theme.colors.theme};
  }
`;

const Empty = styled.div`
  visibility: hidden;
`;

const StyledIcon = styled(Icon)`
  ${p => isObjectNullOrEmpty(p.style) ? 'color: #999' : p.style};
`;

const StyledGrabIcon = styled(Icon)`
  color: #999;
  cursor: grab;
  font-size: 17px;
  margin-top: ${p => p.isDetails ? 6 : 5}px;
`;

const StyledInfo = styled.span`
  margin: 0 3px;
`;

const CircleIcon = styled.div`
  height: 15px;
  width: 15px;
  flex-shrink: 0;
  border: 1px solid ${p => p.color};
  ${(p) => {
    if (p.isTentative) {
      return css`
        background: repeating-linear-gradient(
          120deg,
          #fff,
          #fff 3px,
          ${p.color} 3px,
          ${p.color} 6px
        );
      `;
    }
    return css`
      background: ${p.color};
    `;
  }}
  border-radius: 50%;
`;

const SyncIcon = styled.div`
  background-color: ${p => p.backgroundColor};
  padding: 0px 4px;
  border-radius: 5px;
  height: 20px !important;
  display: flex;
  align-items: center;
  img {
    margin-top: 0px !important;
  }
`;

const DraftIcon = styled(Icon)`
  color: white !important;
  background-color: ${p => p.backgroundColor};
  padding: 2px 4px;
  border-radius: 5px;
  height: 16px;
  line-height: 13px;
`;

const SuccessIcon = styled(Icon)`
  color: ${p => p.theme.colors.publishSteps.finish}
`;

export const getWidth = (id, type, columnCount, columnInfo) => {
  const storedColumnParams = localStorage.getItem(type);
  const storedParams = storedColumnParams ? JSON.parse(storedColumnParams) : [];
  const param = columnInfo?.find(p => p.id === id) || storedParams?.find(p => p.id === id);
  if (param?.width) {
    return param.width;
  }
  if (id === 'Type') {
    return 75;
  }
  if (id === 'TimelineDate' || id === 'Number' || id === 'Status') {
    return 125;
  }
  return (window.innerWidth - 100) / columnCount;
};

const getPublishingIcon = (value, isAssignedPlanner, hasError) => isAssignedPlanner ?
  <DraftIcon
    type='exchange-alt'
    size='sm'
    backgroundColor={hasError ? '#CC0000' : '#F5A623'}
  /> :
  <SyncIcon backgroundColor='#64747c'>
    <Icon
      type='syncLock'
      customIcon={true}
      color='#64747c'
    />
  </SyncIcon>;

const getPublishingSuccessIcon = hasError => hasError ?
  <DraftIcon
    type='exchange-alt'
    size='sm'
    backgroundColor={hasError ? '#CC0000' : '#F5A623'}
  /> :
  <SuccessIcon type='check-circle' />;

export const getDurationsText = (value) => {
  const duration = moment.duration(value, 'hours');
  const days = duration.days();
  const hours = duration.hours();
  const min = duration.minutes();
  return `${days > 0 ? `${days}d ` : ''}${hours}h${min < 10 ? `0${min}` : min}`
    .replace(/00$/, '');
};

export const getStatusIcon = (statusObject) => {
  switch (statusObject) {
    case 1:
      return <InfoIcon type='circle-info' />;
    case 2:
      return <WarningIcon type='exclamation-triangle' />;
    case 3:
      return <AlertIcon type='exclamation-circle' />;
    default:
      return null;
  }
};

export const getValue = (id, value) => {
  const valueDescriptionOptions = [
    'PlanningStatus', 'PublishingStatus', 'LogisticsDate',
  ]?.map(item => item?.toLowerCase());

  if (valueDescriptionOptions.includes(id?.toLowerCase())) {
    return value?.description;
  }
  if (id === 'AdditionalInfo') {
    return value?.map(v => v.description).join('; ');
  }
  if (id === 'Tags') {
    return value?.map(v => `${v.description} (${v.id})`).join('; ');
  }
  if (id === 'Watchers') {
    return value?.map(v => v.displayName).join('; ');
  }
  if (id === 'OrderProcessStatus') {
    return value?.text;
  }
  if (id === 'AssignedWipCodes') {
    return value?.map(v => v.description).join('; ');
  }
  return value;
};

// eslint-disable-next-line complexity
export const getVisualisation = (id, type, value, data, isDetails, showRowAction = true) => {
  if (!value && value !== 0 && type !== 'drag') {
    // No value string is needed otherwise aggrid will set the width to 0
    return <Empty>No value</Empty>;
  }
  if (id === 'Since') {
    return moment().diff(moment(value), 'days');
  }
  if (id === 'OrderProcessStatus') {
    return <Flex rrn aic>
      {getStatusIcon(value?.errorLevel)}
      <StyledInfo>{value?.text}</StyledInfo>
    </Flex>;
  }
  if (id === 'Description') {
    return <LinkWrapper>
      <Link data-test-id='btn-ticket-worklist-description'>{value}</Link>
      {// row quick action are only shown when you didn't selected the row
        showRowAction && <RowActions id={data?.id} actions={data?.actions || []} />
      }
    </LinkWrapper>;
  }
  if (id === 'Date') {
    return <LinkWrapper>
      <Link>{moment(value).format('L')}</Link>
      {// row quick action are only shown when have didn't selected the row
        showRowAction && <RowActions id={data?.id} actions={data?.actions || []} />
      }
    </LinkWrapper>;
  }
  if (id === 'PlannedLabor' || id === 'LaborDuration' ||
    id === 'TravelDuration' || id === 'CalculatedPlannedLabor') {
    return <p>{getDurationsText(value)}</p>;
  }
  if (id === 'PlanningStatus') {
    return <Flex rrn aic>
      <CircleIcon color='#3C85C9' isTentative={value.id === 'Tentative'} />
      <StyledInfo>{value.description}</StyledInfo>
    </Flex>;
  }
  if (id === 'PublishingStatus') {
    return <Flex rrn aic>
      {value.id === 'true' ?
        getPublishingSuccessIcon(data.hasError) :
        getPublishingIcon(value, data.isAssignedPlanner, data.hasError)
      }
      <StyledInfo>{value.description}</StyledInfo>
    </Flex>;
  }
  if (id === 'AdditionalInfo') {
    return value?.map(v => <span key={v.id}>
      <StyledIcon
        className={!v.icon.includes('fa-') ? undefined : v.icon}
        type={v.icon.includes('fa-') ? undefined : v.icon}
        light={!v.icon.includes('solid') && !v.icon.includes('duotone')}
        style={v.iconStyle}
      />
      <StyledInfo> {v.description}</StyledInfo>
    </span>);
  }
  if (id === 'LogisticsDate') {
    return <span>
      <StyledIcon
        type={value?.icon}
        light={!value?.icon?.includes('solid') && !value?.icon?.includes('duotone')}
      />
      <StyledInfo>{value?.description}</StyledInfo>
    </span>;
  }
  if (type === 'sapId') {
    return <SapId originalId={value}>
      {sapId => <span>{sapId}</span>}
    </SapId>;
  }
  if (type === 'date') {
    return value?.endsWith('Z') ?
      moment(value).utc().format('L') :
      moment(moment(value).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')).format('L');
  }

  if (type === 'drag') {
    return <PermissionCheck permissionsList={['planboardmanage']}>
      <StyledGrabIcon className='fa-solid fa-grid row-drag' isDetails={isDetails} />
    </PermissionCheck>;
  }
  if (type === 'tags') {
    return value
      ?.sort((a, b) => originatorTagPrefix?.find(o => b.id.includes(o)) || -1)
      ?.map(v => <Tag key={v.id} color='blue'>
        {id === 'Tags' ? `${v.description} (${v.id})` : v.description}
      </Tag>);
  }
  if (id === 'Watchers') {
    return value?.map(v => v.displayName).join('; ');
  }
  if (id === 'LinkedObject' || id === 'LinkedOrder' || id === 'Customer') {
    return <LinkWrapper>
      <Link>{value}</Link>
    </LinkWrapper>;
  }
  return value;
};

const dragCellRender = (value, linkedId, isDetails, detailProps = {}) => {
  let data = {
    linkedEntityId: linkedId,
    linkedEntityType: isDetails ? 'planning' : 'order',
  };
  if (isDetails) {
    data = {
      ...data,
      ...detailProps,
    };
  }
  return (
    <Draggable
      hasDragStyle={false}
      data={{
        ...data,
        canMove: true,
        isAssignedPlanner: isDetails ? detailProps.isAssignedPlanner : true,
      }}
      type={isDetails ? ItemTypes.PLANNING : ItemTypes.ORDER}
    >
      {value}
    </Draggable>
  );
};

const checkIfHidden = (type, columnInfo, id) => {
  const localData = localStorage.getItem(type);
  const parsedLocalData = localData ? JSON.parse(localData) : [];
  const storedColumnParams = columnInfo?.length ? columnInfo : parsedLocalData;
  return !storedColumnParams?.find(c => c.id === id);
};

export const mapGridColumnDef = (
  columnDef,
  columnActions,
  isDetails,
  type,
  activeSort,
  filterDefinitions,
  worklistId,
  draggable,
  fixedFilterValues = {},
  selection = [],
  columnInfo,
) =>
  columnDef?.map((cd, i) => ({
    isHidden: checkIfHidden(type, columnInfo, cd.id),
    headerName: cd.displayName,
    headerComponent: !isDetails ? CustomHeader : CustomDetailHeader,
    dataIndex: cd.fieldName,
    headerTooltip: cd.displayName,
    tooltipShowDelay: 200,
    colId: cd.id,
    field: cd.fieldName,
    worklistId,
    requiredFeatureFlag: cd.requiredFeatureFlag,
    resizable: true,
    lockPinned: true,
    tableId: isDetails ? cd.childTableId : worklistId,
    isDetails,
    sortable: cd.sortable || isDetails,
    sortingOrder: ['asc', 'desc', null],
    width: getWidth(cd.id, type, columnDef?.length, columnInfo),
    filterId: cd.filterId,
    fixedFilterValues,
    filter: WorkGridFilters,
    cellRenderer: (params) => {
      if (!params?.data) {
        return null;
      }
      const visual = getVisualisation(
        cd.id,
        cd.type,
        params.data[cd.fieldName],
        params.data,
        isDetails,
        !selection?.find(item => item.id === params.data?.id),
      );
      if (draggable && !params.data?.children?.length) {
        return dragCellRender(
          visual,
          params.data?.id,
          isDetails,
          {
            isAssignedPlanner: params.data?.isAssignedPlanner,
            laneId: params.data?.laneId,
            isUserAdminForScope: params.data?.isUserAdminForScope,
            id: params.data?.id,
            objectId: params.data?.objectId,
            showAs: params.data?.showAs,
            type: blockConfig.types.fieldService,
          },
        );
      }
      return visual;
    },
    cellClass: columnActions[cd.id] ? 'cell-actions' : '',
    onCellClicked: columnActions[cd.id] ? ({ data }) => columnActions[cd.id](data) : null,
    valueGetter: params => getValue(cd.id, params.data[cd.fieldName]),
    menuTabs: ['filterMenuTab'],
    index: i,
    icons: {
      sortAscending: determineSortIcon(cd.id, cd.type, true),
      sortDescending: determineSortIcon(cd.id, cd.type, false),
    },
  }));

export const addMasterDetail = columns => [{
  width: 20,
  headerName: '',
  resizable: false,
  suppressMenu: true,
  suppressSizeToFit: true,
  suppressMovable: true,
  lockPosition: 'left',
  cellRenderer: GroupCellRenderer,
  // cellRendererSelector: p => p?.value?.length ? { component: 'agGroupCellRenderer' } : null,
  field: 'children',
  headerComponent: MasterDetailHeader,
  index: 0,
  colId: 'children',
  valueGetter: () => null,
},
...columns?.map(c => ({
  ...c,
  index: c.index + 1,
})),
];

export const addCheckbox = (columns, isDetails = false) => [{
  width: 20,
  headerCheckboxSelection: false,
  checkboxSelection: true,
  resizable: false,
  suppressMenu: true,
  suppressMovable: true,
  suppressSizeToFit: true,
  cellClass: isDetails ? 'ag-details-checkbox' : 'ag-checkbox',
  index: 0,
  colId: 'checkbox',
  lockPosition: 'left',
  valueGetter: () => null,
},
...columns?.map(c => ({
  ...c,
  index: c.index + 1,
})),
];

const getIndex = (storedColumnParams, id, index, hiddenCols, lastVisibleIndex) => {
  if (storedColumnParams?.find(p => p.id === id)) {
    return storedColumnParams?.find(p => p.id === id).index;
  }

  return hiddenCols.indexOf(id) >= 0 ? hiddenCols.indexOf(id) + lastVisibleIndex + 1 : index;
};

export const setOrderColumns = (columns, type, columnInfo, isDetails) => {
  let newColumns = columns;
  const localData = localStorage.getItem(type);
  const parsedLocalData = localData ? JSON.parse(localData) : [];
  const storedColumnParams = columnInfo || parsedLocalData;
  const lastVisibleIndex = storedColumnParams.reduce((prev, current) =>
    current.index > prev.index ? current : prev,
    { index: 0 },
  )?.index;
  const hiddenCols = columns
    ?.filter(c => c.isHidden)
    ?.sort((a, b) => a.index - b.index)
    ?.map(f => f.colId);
  if (storedColumnParams?.length) {
    newColumns = columns?.map((c, i) => !isDetails ?
      {
        ...c,
        index: getIndex(storedColumnParams, c.colId, i, hiddenCols, lastVisibleIndex),
      } :
      {
        ...c,
        tableId: c.tableId,
        width: storedColumnParams?.find(p => p.id === c.colId &&
          p.tableId === c.tableId)?.width || c.width,
        index: storedColumnParams?.find(p =>
          p.id === c.colId && p.tableId === c.tableId)?.index ?? i,
      })?.sort((a, b) => a.index - b.index);
  }
  return newColumns
    ?.filter(c => !c?.requiredFeatureFlag || isToggleEnabled(c?.requiredFeatureFlag));
};

export const makeDraggable = (columns, isDetails) => [{
  width: 20,
  resizable: false,
  suppressMenu: true,
  suppressMovable: true,
  suppressSizeToFit: true,
  lockPosition: 'left',
  valueGetter: () => null,
  cellRenderer: (params) => {
    if (
      !params.data?.children?.length
    ) {
      return dragCellRender(
        getVisualisation('drag', 'drag', null, null, isDetails),
        params.data?.id,
        isDetails,
        {
          isAssignedPlanner: params.data?.isAssignedPlanner,
          laneId: params.data?.laneId,
          isUserAdminForScope: params.data?.isUserAdminForScope,
          id: params.data?.id,
          objectId: params.data?.objectId,
          showAs: params.data?.showAs,
          type: blockConfig.types.fieldService,
        },
      );
    }
    return null;
  },
  index: 0,
  colId: 'drag',
},
...columns?.map(c => ({
  ...c,
  index: c.index + 1,
})),
];

export const addIcons = columns => [
  ...columns,
  {
    colId: 'icons',
    headerName: 'Info',
    suppressMenu: true,
    resizable: false,
    suppressMovable: true,
    suppressSizeToFit: true,
    lockPosition: 'right',
    width: 150,
    cellRenderer: params => params?.data ? <IconColumn data={params.data} /> : null,
    index: columns.length,
    valueGetter: () => null,
  },
];

export const getRowHeight = (
  params,
  rowHeight,
  hasMultipleChildTypes = false,
  childTableIds = [],
) => {
  const isDetailRow = params.node.detail;

  // for all rows that are not detail rows, return nothing
  if (!isDetailRow) { return rowHeight; }

  // If there are multiple children types, we need to take account of the headers per type
  // and increase the height
  if (hasMultipleChildTypes) {
    const types = childTableIds?.filter(c =>
      params.data.children?.find(d => d.type?.toLowerCase() === c?.id?.toLowerCase()),
    ) || [];

    return (params.data.children.length + (types.length * 2)) * 32;
  }
  // otherwise return height based on number of rows in detail grid
  return (params.data.children.length + 1) * 32;
};

export const handleColumnVisibilityEvents = (e, set) => {
  if (e.type === 'columnResized' && e.finished && e.column) {
    const masterEl = document.querySelector('.ag-header');
    const containerEl = document.querySelector('.ag-header-container');
    let width = e.column.actualWidth;
    let reRender = false;
    // The detailscolumns are indented with 55px tothe left
    const detailsLeftOffset = 55;
    if (e.column.colDef.isDetails) {
      if (containerEl?.offsetWidth > masterEl?.offsetWidth) {
        if (
          e.columnApi.columnModel.bodyWidth > containerEl.offsetWidth
        ) {
          // eslint-disable-next-line max-len
          width = e.column.actualWidth - (e.columnApi.columnModel.bodyWidth - (containerEl.offsetWidth - detailsLeftOffset));
          reRender = true;
        }
      } else if (
        e.columnApi.columnModel.bodyWidth > masterEl.offsetWidth - detailsLeftOffset
      ) {
        // eslint-disable-next-line max-len
        width = e.column.actualWidth - (e.columnApi.columnModel.bodyWidth - (masterEl.offsetWidth - detailsLeftOffset));
        reRender = true;
      }
    }
    set(state => state?.map((p) => {
      if (
        e.column?.colId === p.id &&
        p.tableId === e.column.colDef.tableId
      ) {
        return {
          ...p,
          width,
          reRender,
        };
      }
      return p;
    }));
  }

  if (e.type === 'columnMoved') {
    set((state) => {
      // make an distinction between master and sub based on tableId;
      const original = state?.find(c =>
        e.column?.colId === c.id &&
        c.tableId === e.column?.colDef?.tableId,
      );

      if (!original) {
        return state;
      }

      let startIndexElement = 0;
      const startIndices = state?.sort((a, b) => a.index - b.index)?.reduce((tot, curr) => {
        if (tot?.find(t => t.tableId === curr.tableId)) {
          return tot;
        }
        return [...tot, { ...curr, addStartIndex: tot?.length > 0 }];
      }, []);
      startIndexElement = startIndices?.find(s => s.tableId === e.column?.colDef?.tableId);
      const toIndex = e.toIndex + (startIndexElement?.addStartIndex ? startIndexElement?.index : 0);

      return state?.map((p) => {
        if (original.tableId === p.tableId) {
          // set the column that moved to index
          if (original.id === p.id) {
            return {
              ...p,
              index: toIndex,
            };
            // the column('s) that has to go to the back
          } else if (p.index > original.index && p.index <= toIndex) {
            return {
              ...p,
              index: p.index - 1,
            };
            // the column('s) that has to go more forward
          } else if (p.index >= toIndex && p.index < original.index) {
            return {
              ...p,
              index: p.index + 1,
            };
          }
        }
        return p;
      });
    });
  }
};

export const createServerSideDatasource = (
  worklistId,
  fetchFirstData,
  loadMore,
  activeFilters,
  activeSort,
  headersOnly,
) => ({
  getRows: (params) => {
    // To prevent duplicate calls, we already got page 1 when we requested the columns
    if (params.request.startRow === 0) {
      const urlParams = transformFiltersToUrlParams(activeFilters || {});
      fetchFirstData({
        id: worklistId,
        urlString: urlParams,
        columnSort: activeSort,
        needsGeoJson: true,
        shouldFetch: true,
        newWorklist: true,
        cb: nextData => params.success({
          rowData: !headersOnly ? nextData?.data || [] : [],
          rowCount: !nextData?.next ? nextData?.data?.length : null,
        }),
      });
    } else {
      loadMore({
        triggerHighlight: worklistId !== ticketWorklistId,
        cb: nextData => params.success({
          rowData: nextData?.data || [],
          rowCount: !nextData?.next ? params.request.startRow + nextData?.data?.length : null,
        }),
      });
    }
  },
});

export const handleFirstRender = (type, cb) => {
  const colParams = localStorage.getItem(type);
  if (colParams && JSON.parse(colParams)?.length) {
    cb(JSON.parse(colParams));
  }
};

export const handleDetailsSelectionChange = (
  e,
  handleDetailSelectionChange,
  isDetails = false,
) => {
  if (e?.type === 'rowSelected' && isDetails) {
    // isSelected is to prevent an select/deselect when the event is fired by other listeners
    handleDetailSelectionChange(e?.data, e?.node?.isSelected());
  }
};

export const handleSelectionChange = (
  e,
  handleRowSelect,
  disableCb,
  dragCb,
  detailsDragCb,
  isDetails = false,
) => {
  const nodes = e.api.getSelectedNodes();
  if (handleRowSelect) {
    handleRowSelect(nodes?.map(n => n.data));
  }
  if (nodes?.length) {
    if (isDetails) {
      dragCb(true);
      if (nodes.length === 1) {
        detailsDragCb(false);
      } else {
        detailsDragCb(true);
      }
    } else {
      detailsDragCb(true);
      if (nodes?.find(n => n.data?.children?.length)) {
        dragCb(true);
      } else {
        dragCb(false);
      }
    }
    disableCb(true);
  } else {
    disableCb(false);
    dragCb(false);
    detailsDragCb(false);
  }
};

export const handleCellClicked = (e, nonSelectCellName, gridRef, isDetails) => {
  const nodes = gridRef?.api?.getSelectedNodes();
  if (e.column.colId !== nonSelectCellName && e.column.colId !== 'children') {
    if (isDetails) {
      if (!nodes.length) {
        e.node.setSelected(!e.node.selected);
      }
    } else {
      e.node.setSelected(!e.node.selected);
    }
  }
};

export const detailCellRendererParams = (
  ref,
  childColumnDefs,
  childColumnParams,
  rowHeight,
  rowSelection,
  rowDragMultiRow,
  setChildColumnParams,
  handleRowSelect,
  setDisableMasterSelection,
  setSuppressRowDrag,
  setSuppressDetailsRowDrag,
  parentData,
) => ({
  refreshStrategy: 'everything',
  detailGridOptions: {
    columnDefs: childColumnDefs,
    headerHeight: rowHeight,
    rowHeight,
    rowSelection,
    tooltipShowDelay: 1000,
    rowDragMultiRow,
    rowMultiSelectWithClick: true,
    suppressCsvExport: true,
    suppressExcelExport: true,
    suppressHorizontalScroll: true,
    rowClass: 'ag-details-row',
    suppressDragLeaveHidesColumns: true,
    suppressRowClickSelection: true,
    popupParent: document.body,
    onCellClicked: e => handleCellClicked(e, 'Date', ref, true),
    onGridReady: () => {
      handleFirstRender('childColumnParams', setChildColumnParams);
    },
    getRowId: params => params.data.id,
    onColumnMoved: (e) => {
      handleColumnVisibilityEvents(e, setChildColumnParams);
    },
    onColumnResized: (e) => {
      handleColumnVisibilityEvents(e, setChildColumnParams);
    },
    // onRowSelected is need to get data from selected row
    onRowSelected: e => handleDetailsSelectionChange(
      e,
      handleRowSelect,
      true,
    ),
    context: {
      totalChildren: parentData?.data?.totalChildrenCount,
      childrenCount: parentData?.data?.children?.length,
    },
  },
  getDetailRowData: params => params.successCallback(params.data.children),
});

export const smartActionsCellRendererParams = (
  ref,
  childColumnDefs,
  childColumnParams,
  rowHeight,
  rowSelection,
  rowDragMultiRow,
  setChildColumnParams,
  handleRowSelect,
  setDisableMasterSelection,
  setSuppressRowDrag,
  setSuppressDetailsRowDrag,
  parentData,
  childTableIds,
  isRowSelectable,
) => ({
  detailGridOptions: {
    columnDefs: childColumnDefs,
    headerHeight: rowHeight,
    rowHeight,
    rowSelection,
    childTableIds,
    tooltipShowDelay: 1000,
    rowDragMultiRow,
    rowMultiSelectWithClick: true,
    suppressContextMenu: true,
    suppressHorizontalScroll: true,
    rowClass: 'ag-details-row',
    suppressDragLeaveHidesColumns: true,
    suppressRowClickSelection: true,
    popupParent: document.body,
    onCellClicked: e => handleCellClicked(e, 'Date', ref, true),
    onGridReady: () => {
      handleFirstRender('childColumnParams', setChildColumnParams);
    },
    onColumnMoved: (e) => {
      handleColumnVisibilityEvents(e, setChildColumnParams);
    },
    onColumnResized: (e) => {
      handleColumnVisibilityEvents(e, setChildColumnParams);
    },
    // if ticket worklist (so not selectable) ==> null
    onSelectionChanged: isRowSelectable() ? e => handleSelectionChange(
      e,
      handleRowSelect,
      setDisableMasterSelection,
      setSuppressRowDrag,
      setSuppressDetailsRowDrag,
      true,
    ) : null,
    context: {
      totalChildren: parentData?.data?.totalChildrenCount,
      childrenCount: parentData?.data?.children?.length,
    },
  },
  getDetailRowData: params => params.successCallback(params.data.children),
});

export const getAllExpandedRows = (ref) => {
  const expandedRows = [];
  ref.api?.forEachNode((n) => {
    if (n.expanded) {
      expandedRows.push(n.id);
    }
  });
  return expandedRows;
};

export const isRowSelectable = (row) => {
  if (!row?.data?.actions?.length || row?.data?.modelType === 'historicalorder') {
    return false;
  }
  return true;
};

export const planningActions = [
  'confirmPlanningBackground',
  'unConfirmPlanningBackground',
  'publishPlanningBackground',
  'revertPlanningBackground',
  'updatePlanningBackground',
  'deleteUnpublishedPlanningBackground',
];

export const orderActions = [
  'draftOrderBackground',
  'createOrderBackground',
  'releaseOrderBackground',
  'cancelOrderAction',
  'planOrderTentativeBackground',
  'planOrderConfirmedBackground',
  'planOrderBackground',
  'updateOrderBackground',
];

export const ticketActions = [
  'closeTicket',
];

